import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon,
  Language as WebsiteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PublicProfile } from '../../services/publicProfilesService';

const initialsFromName = (first?: string | null, last?: string | null, alias?: string | null) => {
  const f = (first || '').trim();
  const l = (last || '').trim();
  if (f || l) {
    return `${f ? f[0] : ''}${l ? l[0] : ''}`.toUpperCase();
  }
  const a = (alias || '').trim();
  if (a) return a.slice(0, 2).toUpperCase();
  return '?';
};

const cleanWebsite = (url: string) => {
  const u = String(url || '').trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  return `https://${u}`;
};

export type PublicProfileCardProps = {
  profile: PublicProfile;
};

const PublicProfileCard: React.FC<PublicProfileCardProps> = ({ profile }) => {
  const navigate = useNavigate();

  const bannerUrl = profile.banner_image_url ? encodeURI(String(profile.banner_image_url)) : '';

  const displayName = (() => {
    const fn = (profile.first_name || '').trim();
    const ln = (profile.last_name || '').trim();
    if (fn || ln) return `${fn} ${ln}`.trim();
    return profile.alias || 'Member';
  })();

  const subLabel = profile.alias ? `@${profile.alias}` : '';

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.06)',
        background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)',
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 18px 45px rgba(2, 132, 199, 0.14)',
          borderColor: 'rgba(2,132,199,0.18)',
        },
      }}
    >
      <CardActionArea
        onClick={() => navigate(`/user-profile/${profile.id}`)}
        sx={{ alignItems: 'stretch' }}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              height: 92,
              backgroundImage: bannerUrl
                ? `linear-gradient(0deg, rgba(15,23,42,0.25), rgba(15,23,42,0.08)), url("${bannerUrl}")`
                : 'radial-gradient(140px 80px at 20% 20%, rgba(99,102,241,0.28), transparent), radial-gradient(160px 90px at 90% 30%, rgba(14,165,233,0.25), transparent), linear-gradient(135deg, rgba(15,23,42,0.86), rgba(30,41,59,0.86))',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />

          <Avatar
            src={profile.profile_picture_url || undefined}
            alt={displayName}
            sx={{
              width: 64,
              height: 64,
              position: 'absolute',
              left: 16,
              bottom: -32,
              border: '3px solid #fff',
              boxShadow: '0 10px 24px rgba(2, 132, 199, 0.22)',
              bgcolor: 'rgba(99,102,241,0.9)',
              fontWeight: 900,
            }}
          >
            {initialsFromName(profile.first_name, profile.last_name, profile.alias)}
          </Avatar>

          {profile.website ? (
            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
              <Tooltip title={profile.website}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(cleanWebsite(profile.website || ''), '_blank', 'noopener,noreferrer');
                  }}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.88)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.98)' },
                  }}
                  aria-label="open website"
                >
                  <WebsiteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}
        </Box>

        <CardContent sx={{ pt: 5, pb: 2.25 }}>
          <Stack spacing={1.1}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.15,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={displayName}
                >
                  {displayName}
                </Typography>
                {subLabel ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={subLabel}
                  >
                    {subLabel}
                  </Typography>
                ) : null}
              </Box>

              <Chip
                size="small"
                label="Public"
                sx={{
                  fontWeight: 800,
                  bgcolor: 'rgba(34,197,94,0.12)',
                  color: 'rgba(21,128,61,1)',
                  border: '1px solid rgba(34,197,94,0.22)',
                }}
              />
            </Box>

            {profile.location ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                <LocationIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {profile.location}
                </Typography>
              </Box>
            ) : null}

            {profile.bio ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {profile.bio}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {' '}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Open profile">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/user-profile/${profile.id}`);
                  }}
                  sx={{
                    bgcolor: 'rgba(2,132,199,0.08)',
                    color: 'rgba(2,132,199,1)',
                    border: '1px solid rgba(2,132,199,0.14)',
                    '&:hover': { bgcolor: 'rgba(2,132,199,0.12)' },
                  }}
                  aria-label="open profile"
                >
                  <OpenInNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default PublicProfileCard;
